import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import DataTable from '../../components/common/DataTable';
import Button from '../../components/common/Button';
import Badge from '../../components/common/Badge';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { productsApi } from '../../api/products.api';
import { getProductImageUrl } from '../../utils/images';
import { useAuth } from '../../context/AuthContext';
import { PlusIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/outline';

const getColumns = (cacheKey) => [
  {
    key: 'image',
    label: 'Image',
    render: (row) => {
      const baseImageUrl = getProductImageUrl(row.product);
      const imageUrl = baseImageUrl ? `${baseImageUrl}?t=${cacheKey}` : null;
      return (
        <div className="h-12 w-12 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={row.name_en}
              className="h-full w-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <span className="text-gray-400 text-xs hidden items-center justify-center h-full w-full">
            No image
          </span>
        </div>
      );
    }
  },
  { key: 'product', label: 'Product Code' },
  { key: 'name_en', label: 'Name (EN)' },
  {
    key: 'names_es',
    label: 'Names (ES)',
    render: (row) => row.names_es?.join(', ') || '-'
  },
  {
    key: 'type',
    label: 'Type',
    render: (row) => (
      <Badge variant={row.type === 'fruit' ? 'success' : 'info'}>
        {row.type}
      </Badge>
    )
  }
];

export default function ProductsListPage() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [search, setSearch] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [imageCacheKey] = useState(Date.now());

  const fetchProducts = async (page = 1, searchTerm = '') => {
    setLoading(true);
    try {
      const response = await productsApi.getAll({ page, search: searchTerm, limit: 20 });
      setProducts(response.data.data);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts(1, search);
  }, [search]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await productsApi.deleteByCode(deleteId);
      setDeleteId(null);
      fetchProducts(pagination.page, search);
    } catch (error) {
      console.error('Error deleting product:', error);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Products</h1>
        {isAdmin && (
          <Button onClick={() => navigate('/products/new')}>
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Product
          </Button>
        )}
      </div>

      <DataTable
        columns={[
          ...getColumns(imageCacheKey),
          ...(isAdmin ? [{
            key: 'actions',
            label: 'Actions',
            align: 'right',
            render: (row) => (
              <div className="flex gap-2 justify-end">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/products/${row.product}/edit`);
                  }}
                  className="p-1 text-gray-500 hover:text-primary-600"
                >
                  <PencilIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteId(row.product);
                  }}
                  className="p-1 text-gray-500 hover:text-red-600"
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </div>
            )
          }] : [])
        ]}
        data={products}
        loading={loading}
        pagination={pagination}
        onPageChange={(page) => fetchProducts(page, search)}
        onSearch={setSearch}
      />

      <ConfirmDialog
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Delete Product"
        message="Are you sure you want to delete this product?"
        confirmText="Delete"
        loading={deleting}
      />
    </DashboardLayout>
  );
}
